/**
 * Delegate at the desk: someone attending on behalf of a pastor / church / institution.
 * Same data as the public delegate form (who they represent), plus the evidence collected on the
 * spot: photos of the delegate's CI and of the delegation letter, optional signature and the
 * declaration. Registered as a guest flagged `is_delegate`; afterwards the card charges and prints
 * like any other participant.
 */

import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import * as eventGuestsRepo from '@/data/eventGuestsRepository';
import { registerParticipant } from '@/Services/eventParticipants.service';
import { MinisterLookup } from '../../public/components/MinisterLookup';
import { uploadRegistrationDocument } from '../../public/publicRegistrationRepository';
import { missingEvidence, type DeskEvidence } from '../deskLogic';
import { DeskEvidenceFields } from './DeskEvidenceFields';

interface DeskDelegateDialogProps {
  eventId: string;
  initialDni: string;
  declarationText?: string | null;
  /** The event signs digitally: the signature is mandatory */
  signatureRequired?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRegistered: () => void;
}

export function DeskDelegateDialog({
  eventId,
  initialDni,
  declarationText,
  signatureRequired = false,
  open,
  onOpenChange,
  onRegistered,
}: DeskDelegateDialogProps) {
  const { toast } = useToast();
  const [fullName, setFullName] = useState('');
  const [dni, setDni] = useState(initialDni);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [represented, setRepresented] = useState<{ id: string; label: string } | null>(null);
  const [manual, setManual] = useState(false);
  const [representedName, setRepresentedName] = useState('');
  const [representedCi, setRepresentedCi] = useState('');
  const [institutionNote, setInstitutionNote] = useState('');
  const [evidence, setEvidence] = useState<DeskEvidence>({ declaration: false, signature: null });
  const [busy, setBusy] = useState(false);

  const representsSomeone = !!represented || representedName.trim().length > 0;
  const missing = missingEvidence('DELEGATE', evidence, signatureRequired);
  const canSubmit =
    fullName.trim().length >= 3 &&
    dni.replace(/\D/g, '').length >= 5 &&
    phone.trim() !== '' &&
    representsSomeone &&
    missing.length === 0;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const rep = {
        represented_name: represented ? '' : representedName.trim(),
        represented_ci: represented ? '' : representedCi.trim(),
        institution_note: institutionNote.trim(),
      };
      const guest = await eventGuestsRepo.findOrCreateEventGuest({
        full_name: fullName.trim(),
        dni: dni.trim(),
        email: email.trim() || undefined,
        phone: phone.trim(),
        representing_minister_id: represented?.id,
        is_delegate: true,
        ...rep,
      });
      const documents: Record<string, string> = {
        ci_front: evidence.ci_front as string,
        ci_back: evidence.ci_back as string,
        delegation_letter: evidence.delegation_letter as string,
      };
      if (evidence.signature) {
        documents.signature = await uploadRegistrationDocument(eventId, 'signature', evidence.signature);
      }
      await registerParticipant({
        event_id: eventId,
        guest_id: guest.id,
        calculate_price: true,
        documents,
        declaration_accepted: evidence.declaration,
        form_signed: !!evidence.signature,
        registration_data: {
          kind: 'DELEGATE',
          full_name: fullName.trim(),
          dni: dni.trim(),
          cell_phone: phone.trim(),
          email: email.trim() || undefined,
          represented_minister_id: represented?.id ?? null,
          ...rep,
        },
      });
      toast({ title: 'Delegado inscrito' });
      onOpenChange(false);
      onRegistered();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'No se pudo inscribir', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar delegado</DialogTitle>
          <DialogDescription>
            Persona que asiste en representación de un ministro, iglesia o institución. Tome las fotos de su CI y de la
            carta; después podrá cobrar y entregar su credencial.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="del-name">Nombre completo *</Label>
              <Input id="del-name" value={fullName} onChange={(e) => setFullName(e.target.value)} autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="del-ci">Nº de CI *</Label>
              <Input id="del-ci" value={dni} onChange={(e) => setDni(e.target.value)} inputMode="text" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="del-phone">Celular *</Label>
              <Input id="del-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="del-email">E-mail</Label>
              <Input id="del-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Opcional" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>¿A quién representa? *</Label>
            {represented ? (
              <div className="flex items-center justify-between rounded-md border p-3 text-sm">
                <span>{represented.label}</span>
                <Button type="button" variant="ghost" size="icon" onClick={() => setRepresented(null)} aria-label="Quitar">
                  <X className="size-4" />
                </Button>
              </div>
            ) : manual ? (
              <div className="space-y-3">
                <Input
                  value={representedName}
                  onChange={(e) => setRepresentedName(e.target.value)}
                  placeholder="Nombre de la persona, iglesia o institución"
                />
                <Input
                  value={representedCi}
                  onChange={(e) => setRepresentedCi(e.target.value)}
                  placeholder="Nº de CI del pastor / representante (opcional)"
                />
                <Input
                  value={institutionNote}
                  onChange={(e) => setInstitutionNote(e.target.value)}
                  placeholder="Detalle (ej. Exploradores — Región La Paz)"
                />
                <Button type="button" variant="link" className="h-auto p-0" onClick={() => setManual(false)}>
                  Buscar ministro en su lugar
                </Button>
              </div>
            ) : (
              <>
                <MinisterLookup
                  eventId={eventId}
                  placeholder="Nombre, CI o código del ministro que representa"
                  onSelect={(m) => setRepresented({ id: m.id, label: `${m.full_name} (${m.adb_code})` })}
                />
                <Button type="button" variant="link" className="h-auto p-0" onClick={() => setManual(true)}>
                  Representa a una iglesia, institución u otra persona
                </Button>
              </>
            )}
          </div>

          <DeskEvidenceFields
            eventId={eventId}
            kind="DELEGATE"
            value={evidence}
            onChange={setEvidence}
            declarationText={declarationText}
            disabled={busy}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={busy || !canSubmit}>
            {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
            Inscribir delegado
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
